import React from 'react';
import { Box, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CustomLazyLoadImage from '../../../Image/CustomLazyLoadImage';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CINEMA_ON_SURFACE, CINEMA_PRIMARY, ghostBorderSx, glassCardSx } from '../../../WatchPage/watchCinemaTheme';
import { IManageContestSubmissions } from '../../../../redux/contest/types';
import { FullScreenMoreMenu } from '../../../More/FullScreenMoreMenu';
import { TMoreMenuItem } from '../../../More/more.data';
import { formatJuryScore, judgingStatusVisual, pulseDot } from './submissionObsidianShared';
import { brandContestTableCellTextSx } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { brandSubmissionGridSx, brandSubmissionMobileLabelSx } from './brandSubmissionCardLayout';

const VALUE_SX = {
    ...brandContestTableCellTextSx,
    fontSize: { xs: 14, lg: 14 },
    color: CINEMA_ON_SURFACE,
} as const;

const METRIC_SX = {
    ...VALUE_SX,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontWeight: 600,
    fontSize: { xs: 15, lg: 15 },
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
} as const;

/** Icon trigger — flat on mobile; sits inside glass square on md+ */
const MORE_ICON_BUTTON_SX = {
    p: { xs: 0.25, lg: 0.5 },
    minWidth: 0,
    width: { xs: 32, lg: 40 },
    height: { xs: 32, lg: 40 },
    borderRadius: { xs: 1, lg: 2 },
    bgcolor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
} as const;

const MOBILE_SECTION_SX = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
} as const;

export interface BrandContestSubmissionCardProps {
    submission: IManageContestSubmissions;
    dateLabel: string;
    menuItems: TMoreMenuItem[];
    onView: (submission: IManageContestSubmissions) => void;
    onMenuClick: (type: string, submission: IManageContestSubmissions) => void;
    onVisitUser: (submission: IManageContestSubmissions) => void;
}

const BrandContestSubmissionCard: React.FC<BrandContestSubmissionCardProps> = ({
    submission,
    dateLabel,
    menuItems,
    onView,
    onMenuClick,
    onVisitUser,
}) => {
    const status = submission.payment_due
        ? {
              label: 'Payment Due',
              bg: 'rgba(245, 158, 11, 0.12)',
              color: '#fbbf24',
              border: 'rgba(245, 158, 11, 0.35)',
              dot: '#f59e0b',
              pulse: true,
          }
        : judgingStatusVisual(submission.judging_status);
    const project = submission.project;
    const votes = submission.totalVotes ?? 0;
    const juryDisplay = formatJuryScore(submission.averageJuryScore);

    return (
        <Box
            sx={{
                ...glassCardSx,
                ...ghostBorderSx,
                borderRadius: 4,
                overflow: 'hidden',
                maxWidth: '100%',
                boxSizing: 'border-box',
                transition: 'background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    bgcolor: 'rgba(42, 42, 42, 0.55)',
                    transform: { xs: 'none', lg: 'translateY(-2px)' },
                    boxShadow: { xs: 'none', lg: '0 12px 28px rgba(0, 0, 0, 0.35)' },
                },
            }}>
            <Box
                sx={{
                    ...brandSubmissionGridSx,
                    py: { xs: 2.5, lg: 2.5 },
                    minHeight: { lg: 108 },
                }}>
                {/* Project — wide & centered on mobile; fixed column on md+ */}
                <Box
                    sx={{
                        ...MOBILE_SECTION_SX,
                        gap: 1,
                        justifySelf: { lg: 'center' },
                        alignItems: 'center',
                        width: { xs: '100%', lg: 112 },
                        maxWidth: { xs: 360, lg: 112 },
                    }}>
                    <Typography sx={{ ...brandSubmissionMobileLabelSx, display: { xs: 'block', lg: 'none' } }}>
                        Project
                    </Typography>
                    <Box
                        sx={{
                            position: 'relative',
                            width: { xs: '100%', lg: 112 },
                            height: { xs: 140, sm: 160, lg: 72 },
                            flexShrink: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 16px 32px rgba(0,0,0,0.45)',
                            cursor: 'pointer',
                            '&:hover img': { transform: 'scale(1.04)' },
                        }}
                        onClick={() => onView(submission)}>
                        <CustomLazyLoadImage
                            src={project?.feature_image}
                            alt={project?.title ?? 'Submission'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.5s ease',
                            }}
                            gradientStyle={{ borderRadius: 8 }}
                        />
                    </Box>
                    <Box
                        sx={{
                            minWidth: 0,
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                            alignItems: 'center',
                            textAlign: 'center',
                        }}>
                        <Typography
                            onClick={() => onVisitUser(submission)}
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: { xs: 13, lg: 11 },
                                color: `${CINEMA_ON_SURFACE}b3`,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                lineHeight: 1.3,
                                '&:hover': { color: CINEMA_PRIMARY },
                            }}>
                            @{submission.created_by?.userName ?? 'Unknown User'}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: { xs: 12, lg: 10 },
                                color: '#99907c',
                                lineHeight: 1.35,
                                maxWidth: '100%',
                            }}>
                            {dateLabel}
                        </Typography>
                    </Box>
                </Box>

                {/* Title — centered on mobile; sits between project & status on md+ */}
                <Box
                    sx={{
                        ...MOBILE_SECTION_SX,
                        justifySelf: { lg: 'stretch' },
                        alignItems: 'center',
                        overflow: 'hidden',
                    }}>
                    <Typography sx={{ ...brandSubmissionMobileLabelSx, display: { xs: 'block', lg: 'none' } }}>
                        Title
                    </Typography>
                    <Typography
                        onClick={() => onView(submission)}
                        sx={{
                            ...VALUE_SX,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            fontSize: { xs: 16, lg: 15 },
                            lineHeight: 1.35,
                            cursor: 'pointer',
                            textAlign: 'center',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: { xs: 'normal', lg: 'nowrap' },
                            display: { xs: '-webkit-box', lg: 'block' },
                            WebkitLineClamp: { xs: 3, lg: 'unset' },
                            WebkitBoxOrient: 'vertical',
                            '&:hover': { color: CINEMA_PRIMARY },
                        }}>
                        {project?.title ?? '—'}
                    </Typography>
                </Box>

                {/* Judging Status */}
                <Box sx={{ ...MOBILE_SECTION_SX, justifySelf: { lg: 'center' } }}>
                    <Typography
                        sx={{
                            ...brandSubmissionMobileLabelSx,
                            display: { xs: 'block', lg: 'none' },
                            mb: { xs: 1.25, lg: 0 },
                        }}>
                        Judging Status
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: status.bg,
                            color: status.color,
                            border: `1px solid ${status.border}`,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            fontSize: 11,
                            textTransform: 'capitalize',
                            whiteSpace: 'nowrap',
                        }}>
                        <Box
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                flexShrink: 0,
                                bgcolor: status.dot,
                                boxShadow: `0 0 6px ${status.dot}`,
                                ...(status.pulse ? { animation: `${pulseDot} 2s ease-in-out infinite` } : {}),
                            }}
                        />
                        {status.label}
                    </Box>
                </Box>

                {/* Votes + Jury Score + Actions — centered group below lg; lg+ separate grid cols */}
                <Box
                    sx={{
                        gridColumn: { xs: '1', lg: 'auto' },
                        display: { xs: 'flex', lg: 'contents' },
                        justifyContent: { xs: 'center', lg: 'unset' },
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                    }}>
                    <Box
                        sx={{
                            display: { xs: 'inline-grid', lg: 'contents' },
                            gridTemplateColumns: { xs: 'auto auto auto', lg: 'unset' },
                            gridTemplateRows: { xs: 'auto auto', lg: 'unset' },
                            columnGap: { xs: 5, sm: 6 },
                            rowGap: { xs: 0.5, lg: 0 },
                            justifyItems: 'center',
                            alignItems: 'center',
                        }}>
                        <Typography
                            sx={{
                                ...brandSubmissionMobileLabelSx,
                                display: { xs: 'block', lg: 'none' },
                                gridColumn: 1,
                                gridRow: 1,
                            }}>
                            Votes
                        </Typography>
                        <Typography
                            sx={{
                                ...brandSubmissionMobileLabelSx,
                                display: { xs: 'block', lg: 'none' },
                                gridColumn: 2,
                                gridRow: 1,
                            }}>
                            Jury Score
                        </Typography>
                        <Typography
                            sx={{
                                ...brandSubmissionMobileLabelSx,
                                display: { xs: 'block', lg: 'none' },
                                gridColumn: 3,
                                gridRow: 1,
                            }}>
                            Actions
                        </Typography>

                        <Typography
                            sx={{
                                ...METRIC_SX,
                                gridColumn: { xs: 1, lg: 'auto' },
                                gridRow: { xs: 2, lg: 'auto' },
                                justifySelf: { lg: 'center' },
                            }}>
                            {votes}
                        </Typography>
                        <Typography
                            sx={{
                                ...METRIC_SX,
                                color: CINEMA_PRIMARY,
                                gridColumn: { xs: 2, lg: 'auto' },
                                gridRow: { xs: 2, lg: 'auto' },
                                justifySelf: { lg: 'center' },
                            }}>
                            {juryDisplay}
                        </Typography>

                        <Box
                            sx={{
                                gridColumn: { xs: 3, lg: 'auto' },
                                gridRow: { xs: 2, lg: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                justifySelf: { lg: 'center' },
                                width: { xs: 'auto', lg: 40 },
                                height: { xs: 32, lg: 40 },
                                borderRadius: { lg: 2 },
                                border: { xs: 'none', lg: '1px solid rgba(153, 144, 124, 0.2)' },
                                bgcolor: { xs: 'transparent', lg: 'rgba(42, 42, 42, 0.4)' },
                                backdropFilter: { xs: 'none', lg: 'blur(24px)' },
                                WebkitBackdropFilter: { xs: 'none', lg: 'blur(24px)' },
                                boxShadow: { xs: 'none', lg: 'none' },
                                flexShrink: 0,
                            }}>
                            <FullScreenMoreMenu
                                menu={menuItems}
                                onClick={type => onMenuClick(type, submission)}
                                customIconSx={MORE_ICON_BUTTON_SX}
                                customMoreIcon={
                                    <MoreHorizIcon
                                        sx={{
                                            transform: 'rotate(180deg)',
                                            color: CINEMA_ON_SURFACE,
                                            fontSize: { xs: 24, lg: 22 },
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export { BrandContestSubmissionCard };
